// The Inbox: documents that arrived by email, waiting to become contracts. The same editor runs in
// the app and on the page an email's Edit link opens (without signing in).
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { mergeAiSuggestions } from "../../shared/ai";
import { typeFor } from "../../shared/catalog";
import { emptyContractFields, type ContractFields } from "../../shared/contract";
import { formatDate } from "../../shared/dates";
import { draftFromRules } from "../../shared/draft";
import { changeLine, contractChanges, type FieldChange } from "../../shared/changes";
import { aiNoteLines } from "../../shared/inbox";
import { dateOrderForLocale, suggestFromText, type Suggestions } from "../../shared/suggest";
import { ContractForm } from "../components/ContractForm";
import { Alert, Badge, Button, Card, CardTitle, Dialog, EmptyState, Field, PageHeader, Select, Spinner, buttonClass, cx, focusRing, formatBytes } from "../components/ui";
import { aiExtras, creditsLine, readWithAi } from "../lib/ai";
import { extractText } from "../lib/extract";
import {
  acceptItem,
  applyItem,
  attachItem,
  discardItem,
  fetchItemFile,
  fromLine,
  getAddress,
  getItem,
  itemBase,
  listInbox,
  restoreItem,
  type InboxItem,
  type ItemApi,
  type ItemStatus,
  type ReadText,
} from "../lib/inbox";
import { usualCurrency } from "../lib/display";
import { engine, useContract, useContracts, useOnline, useSession } from "../lib/session";

const TABS: { status: ItemStatus; label: string }[] = [
  { status: "waiting", label: "Waiting" },
  { status: "accepted", label: "Saved" },
  { status: "discarded", label: "Discarded" },
];

/** "Rent agreement · Flat 4B lease" from what was found, or what's still to happen. */
function summary(i: InboxItem): string {
  if (i.processing) return "Reading it…";
  if (i.kind === "update") {
    const n = i.changes?.length ?? 0;
    return `Update to ${i.target?.title ?? "a contract"} · ${n ? `${n} change${n === 1 ? "" : "s"}` : "no changes, just the file"}`;
  }
  const f = i.draft?.fields;
  const bits = [typeFor(f?.type)?.label, f?.title && f.title !== i.filename.replace(/\.(pdf|docx)$/i, "") ? f.title : null].filter(Boolean);
  if (!i.hasText) return i.aiNote?.status === "no_text" ? "A scan: open it to read it on this device" : "Not read yet: open it to fill in the details";
  return bits.length ? bits.join(" · ") : `${i.draft?.found ?? 0} details found`;
}

function FilledBadge({ item }: { item: InboxItem }) {
  if (item.processing) return <Badge>Reading…</Badge>;
  if (item.kind === "update") return <Badge tone="info">Update</Badge>;
  if (!item.hasText) return <Badge tone="warn">{item.aiNote?.status === "no_text" ? "Scan" : "Not read yet"}</Badge>;
  return item.filledBy === "ai" ? <Badge tone="info">Filled by AI</Badge> : <Badge>Filled without AI</Badge>;
}

export function Inbox() {
  const { me, refreshMe } = useSession();
  const [status, setStatus] = useState<ItemStatus>("waiting");
  const [data, setData] = useState<{ items: InboxItem[]; counts: Record<ItemStatus, number> } | null>(null);
  const [address, setAddress] = useState<string | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await listInbox(status));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the Inbox.");
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    getAddress()
      .then((a) => setAddress(a.address))
      .catch(() => setAddress(null));
  }, []);
  // Something still being read: look again shortly.
  useEffect(() => {
    if (!data?.items.some((i) => i.processing)) return;
    const t = setTimeout(() => void load(), 5000);
    return () => clearTimeout(t);
  }, [data, load]);

  async function restore(id: string) {
    try {
      await restoreItem(id);
      await load();
      void refreshMe();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't restore it.");
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Inbox" description="Contracts you emailed to ClauseKeep. Nothing becomes a contract until you save it." />
      {me?.inbox?.enabled && address === null && (
        <Alert tone="info">
          Get your private email address in <Link to="/settings#email-in" className="font-medium underline underline-offset-2">Settings</Link>, then forward contracts to it.
        </Alert>
      )}
      {address && (
        <p className="text-sm text-muted">
          Forward contracts to <span className="break-all font-medium text-fg">{address}</span>
        </p>
      )}
      <div role="tablist" aria-label="Inbox" className="flex gap-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.status}
            role="tab"
            aria-selected={status === t.status}
            onClick={() => setStatus(t.status)}
            className={cx(
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium",
              focusRing,
              status === t.status ? "border-accent text-fg" : "border-transparent text-muted hover:text-fg",
            )}
          >
            {t.label}
            {data && data.counts[t.status] > 0 && <span className="ml-1.5 tabular-nums text-faint">{data.counts[t.status]}</span>}
          </button>
        ))}
      </div>
      {error && <Alert tone="bad">{error}</Alert>}
      {!data ? (
        <Spinner />
      ) : data.items.length === 0 ? (
        <EmptyState title={<p className="font-medium text-fg">{status === "waiting" ? "Nothing waiting" : status === "accepted" ? "Nothing saved from email yet" : "Nothing discarded"}</p>}>
          {status === "waiting" ? "Email a PDF or Word contract to your ClauseKeep address and it shows up here, filled in." : null}
        </EmptyState>
      ) : (
        <ul className="divide-y divide-line rounded-xl border border-line bg-surface">
          {data.items.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                {status === "waiting" ? (
                  <Link to={`/inbox/${i.id}`} className={cx("font-medium text-fg hover:underline", focusRing)}>
                    {i.filename}
                  </Link>
                ) : (
                  <span className="font-medium text-fg">{i.filename}</span>
                )}
                <div className="truncate text-xs text-faint">
                  {fromLine(i)} · {formatDate(i.createdAt.slice(0, 10))}
                </div>
                <div className="truncate text-sm text-muted">
                  {status === "accepted" && i.contract ? (
                    i.contract.deleted ? (
                      "The contract was deleted later."
                    ) : (
                      <Link to={`/contracts/${i.contract.id}`} className="font-medium text-accent-fg hover:underline">
                        {i.appliedChanges ? `Changes applied to ${i.contract.title}` : i.acceptedHow === "attached" ? `Added to ${i.contract.title}` : i.contract.title}
                      </Link>
                    )
                  ) : (
                    summary(i)
                  )}
                </div>
              </div>
              {status === "waiting" && <FilledBadge item={i} />}
              {status === "accepted" && i.decidedVia && <Badge>{i.decidedVia === "app" ? "Saved in the app" : "Saved from the email"}</Badge>}
              {status === "discarded" && (
                <Button variant="secondary" size="sm" onClick={() => void restore(i.id)}>
                  Restore
                </Button>
              )}
              {i.deletesOn && <span className="w-full text-xs text-faint sm:w-auto">Deleted on {formatDate(i.deletesOn)}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** What AI did, said kindly, above the form. */
function AiNoteAlert({ item, extra }: { item: InboxItem; extra?: string | null }) {
  const lines = aiNoteLines(item.aiNote, "app");
  if (!lines.length && !extra) return null;
  return (
    <Alert tone={item.aiNote?.status === "ai" ? "info" : "warn"}>
      {lines.map((l, n) => (
        <span key={n}>
          {l.text}{" "}
          {l.buy && (
            <Link to="/settings#buy-credits" className="font-medium underline underline-offset-2">
              Buy credits
            </Link>
          )}{" "}
        </span>
      ))}
      {extra}
    </Alert>
  );
}

interface EditorProps {
  where: ItemApi;
  item: InboxItem;
  today: string;
  /** Signed in: AI can read a scan, and the item can be added to an existing contract. */
  signedIn: boolean;
  onSaved: (contractId: string) => void;
  onDiscarded: () => void;
  onCancel: () => void;
}

/** The new-contract form, filled from an inbox item, with Save, Add to existing and Discard. */
export function ItemEditor({ where, item, today, signedIn, onSaved, onDiscarded, onCancel }: EditorProps) {
  const { me } = useSession();
  const contracts = useContracts();
  const online = useOnline();
  const [initial, setInitial] = useState<ContractFields>(item.draft?.fields ?? { ...emptyContractFields(), title: item.filename.replace(/\.(pdf|docx)$/i, "") });
  const [aiKeys, setAiKeys] = useState<Set<string>>(new Set(item.draft?.aiKeys ?? []));
  const [suggestions, setSuggestions] = useState<Suggestions>(item.suggestions ?? {});
  const [formKey, setFormKey] = useState(0);
  const [reading, setReading] = useState<string | null>(null);
  const [readText, setReadText] = useState<ReadText | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attachTo, setAttachTo] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const needsReading = !item.hasText && !item.processing;

  // A scan (or a server without AI): read it here, like an upload, then let AI fill the rest.
  useEffect(() => {
    if (!needsReading) return;
    let cancelled = false;
    (async () => {
      try {
        setReading("Reading the document…");
        const file = await fetchItemFile(where, item);
        const result = await extractText(file, (m) => !cancelled && setReading(m));
        if (cancelled) return;
        setReadText({ text: result.text, method: result.method });
        const s = suggestFromText(result.text, dateOrderForLocale(navigator.language), { ownNames: [me?.org.name, me?.user.name].filter((n): n is string => !!n) });
        let fields = draftFromRules(initial, s, item.filename);
        setSuggestions(s);
        if (signedIn && online && result.text.trim().length >= 40) {
          setReading("Reading with AI (quick scan)…");
          const ai = await readWithAi(result.text);
          if (cancelled) return;
          if (ai.ok) {
            const merged = mergeAiSuggestions(fields, ai.suggestions);
            fields = merged.fields;
            setAiKeys(merged.aiKeys);
            setNote(`AI filled in ${merged.filled} more detail${merged.filled === 1 ? "" : "s"}. ${creditsLine(ai.charged, ai.credits)} ${aiExtras(ai)}`.trim());
          } else if (!ai.quiet) setNote(ai.message);
        }
        setInitial(fields);
        setFormKey((k) => k + 1);
      } catch (err) {
        if (!cancelled) setError(`${err instanceof Error ? err.message : "Couldn't read the file."} You can still fill in the details yourself.`);
      } finally {
        if (!cancelled) setReading(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Once per item.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  async function save(fields: ContractFields) {
    const { contractId } = await acceptItem(where, fields, needsReading ? readText : null);
    onSaved(contractId);
  }

  async function attach() {
    setError(null);
    try {
      await attachItem(item.id, attachTo, needsReading ? readText : null);
      onSaved(attachTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't add it.");
    }
  }

  async function discard() {
    setConfirmDiscard(false);
    try {
      await discardItem(where);
      onDiscarded();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't discard it.");
    }
  }

  return (
    <div className="space-y-4">
      <Card className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{item.filename}</div>
          <div className="text-xs text-faint">
            {fromLine(item)} · {formatBytes(item.sizeBytes)}
            {item.subject ? ` · “${item.subject}”` : ""}
          </div>
        </div>
        {item.contentType === "application/pdf" && (
          <a className="text-sm font-medium text-accent-fg hover:underline" href={`${itemBase(where)}/file?inline=1`} target="_blank" rel="noopener">
            View
          </a>
        )}
        <a className="text-sm font-medium text-accent-fg hover:underline" href={`${itemBase(where)}/file`} download={item.filename}>
          Download
        </a>
      </Card>
      {item.replaces && (
        <Alert tone="info">
          This replaces {item.replaces.filename}, an earlier version from the same email thread. It's kept under Discarded for 30 days.
          {item.changes && item.changes.length > 0 && <ChangeList changes={item.changes} title="Changed from the earlier version:" />}
        </Alert>
      )}
      {!needsReading && <AiNoteAlert item={item} />}
      {note && <Alert tone="info">{note}</Alert>}
      {error && <Alert tone="warn">{error}</Alert>}
      {reading ? (
        <Spinner label={reading} />
      ) : (
        <Card>
          <ContractForm
            key={formKey}
            initial={initial}
            suggestedType={suggestions.type?.value}
            aiKeys={aiKeys}
            defaultCurrency={contracts ? usualCurrency(contracts) : ""}
            members={me?.members ?? []}
            currentUserId={me?.user.id}
            suggestions={suggestions}
            today={today}
            submitLabel="Save contract"
            onSubmit={save}
            onCancel={onCancel}
          />
        </Card>
      )}
      {signedIn && contracts && contracts.length > 0 && (
        <Card className="space-y-3">
          <CardTitle description="An amendment, a signed copy or an annex: add the file to a contract you already have.">Or add it to an existing contract</CardTitle>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-0 flex-1">
              <Field label="Contract">
                {(fieldId) => (
                  <Select id={fieldId} value={attachTo} onChange={(e) => setAttachTo(e.target.value)}>
                    <option value="">Choose a contract…</option>
                    {[...contracts]
                      .sort((a, b) => a.title.localeCompare(b.title))
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.title}
                        </option>
                      ))}
                  </Select>
                )}
              </Field>
            </div>
            <Button variant="secondary" disabled={!attachTo || !!reading} onClick={() => void attach()}>
              Add to contract
            </Button>
          </div>
        </Card>
      )}
      <div className="flex justify-end">
        <Button variant="ghost" onClick={() => setConfirmDiscard(true)}>
          Discard
        </Button>
      </div>
      <Dialog open={confirmDiscard} title="Discard this document?" onClose={() => setConfirmDiscard(false)}>
        <p className="text-sm">It won't become a contract. You can restore it from Discarded for 30 days; after that the file is deleted.</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmDiscard(false)}>
            Keep it
          </Button>
          <Button variant="danger" onClick={() => void discard()}>
            Discard
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function ChangeList({ changes, title }: { changes: FieldChange[]; title: string }) {
  return (
    <div className="mt-1">
      <p className="font-medium">{title}</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5">
        {changes.map((c) => (
          <li key={c.field}>{changeLine(c)}</li>
        ))}
      </ul>
    </div>
  );
}

/** An update from the email thread: what it changes in its contract, and Apply changes. */
function UpdateReview({ item, today, onApplied, onSavedAsNew, onDiscarded }: { item: InboxItem; today: string; onApplied: (id: string) => void; onSavedAsNew: (id: string) => void; onDiscarded: () => void }) {
  const [separate, setSeparate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const target = useContract(item.target?.id);
  // A scan (or a server without AI): read it here and work out the changes against the contract.
  const needsReading = !item.hasText && !item.processing;
  const [read, setRead] = useState<{ fields: ContractFields; text: ReadText } | null>(null);
  const [reading, setReading] = useState<string | null>(null);
  useEffect(() => {
    if (!needsReading) return;
    let cancelled = false;
    (async () => {
      try {
        setReading("Reading the document…");
        const result = await extractText(await fetchItemFile({ kind: "app", id: item.id }, item), (m) => !cancelled && setReading(m));
        if (cancelled) return;
        const s = suggestFromText(result.text, dateOrderForLocale(navigator.language));
        setRead({ fields: draftFromRules(emptyContractFields(), s, item.filename), text: { text: result.text, method: result.method } });
      } catch (err) {
        if (!cancelled) setError(`${err instanceof Error ? err.message : "Couldn't read the file."} You can still add it to the contract.`);
      } finally {
        if (!cancelled) setReading(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Once per item.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);
  const changes = read ? (target ? contractChanges(target, read.fields) : []) : (item.changes ?? []);
  if (separate) {
    return (
      <div className="space-y-3">
        <Alert tone="info">Saving it as a separate contract. {item.target?.title ?? "The contract it updates"} stays as it is.</Alert>
        <ItemEditor where={{ kind: "app", id: item.id }} item={item} today={today} signedIn onSaved={onSavedAsNew} onDiscarded={onDiscarded} onCancel={() => setSeparate(false)} />
      </div>
    );
  }
  async function apply() {
    setBusy(true);
    setError(null);
    try {
      onApplied((await applyItem(item.id, read ?? undefined)).contractId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't apply the changes.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-4">
      <Card className="space-y-3">
        <CardTitle description={`${item.filename} · ${fromLine(item)}`}>
          Update to{" "}
          {item.target && !item.target.deleted ? (
            <Link to={`/contracts/${item.target.id}`} className="underline underline-offset-2">
              {item.target.title}
            </Link>
          ) : (
            "a deleted contract"
          )}
        </CardTitle>
        {reading ? (
          <Spinner label={reading} />
        ) : changes.length ? (
          <ChangeList changes={changes} title="What changes:" />
        ) : (
          <p className="text-sm text-muted">We didn't find any changes to the details. You can still add the file to the contract.</p>
        )}
        {!needsReading && <AiNoteAlert item={item} />}
        {error && <Alert tone="bad">{error}</Alert>}
        <div className="flex flex-wrap gap-2">
          <Button disabled={busy || !!reading || !item.target || item.target.deleted} onClick={() => void apply()}>
            {busy ? "Applying…" : changes.length ? "Apply changes" : "Add the file to the contract"}
          </Button>
          <Button variant="secondary" onClick={() => setSeparate(true)}>
            Save as a separate contract
          </Button>
          <a className={buttonClass("ghost")} href={`/api/inbox/${item.id}/file?inline=1`} target="_blank" rel="noopener">
            View the file
          </a>
        </div>
      </Card>
      <div className="flex justify-end">
        <Button
          variant="ghost"
          onClick={async () => {
            await discardItem({ kind: "app", id: item.id });
            onDiscarded();
          }}
        >
          Ignore it
        </Button>
      </div>
    </div>
  );
}

/** /inbox/:id — one waiting document, in the app. */
export function InboxItemPage() {
  const { id } = useParams();
  const { today, refreshMe } = useSession();
  const navigate = useNavigate();
  const [item, setItem] = useState<InboxItem | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    getItem({ kind: "app", id })
      .then((r) => setItem(r.item))
      .catch((err) => {
        setItem(null);
        setError(err instanceof Error ? err.message : "Couldn't load it.");
      });
  }, [id]);

  if (item === undefined) return <Spinner />;
  if (!item || !id) return <Alert tone="bad">{error ?? "That document isn't in your Inbox."}</Alert>;
  const back = (
    <Link to="/inbox" className={buttonClass("secondary", "sm")}>
      Back to the Inbox
    </Link>
  );
  if (item.status !== "waiting") {
    return (
      <div className="space-y-4">
        <PageHeader title={item.filename} actions={back} />
        {item.status === "accepted" && item.contract ? (
          <Alert tone="good">
            Saved as a contract.{" "}
            {!item.contract.deleted && (
              <Link to={`/contracts/${item.contract.id}`} className="font-medium underline underline-offset-2">
                Open it
              </Link>
            )}
          </Alert>
        ) : (
          <Alert tone="info">This was discarded. You can restore it from the Inbox.</Alert>
        )}
      </div>
    );
  }
  const saved = async (contractId: string) => {
    await engine.sync();
    void refreshMe();
    navigate(`/contracts/${contractId}`);
  };
  const discarded = () => {
    void refreshMe();
    navigate("/inbox");
  };
  if (item.kind === "update") {
    return (
      <div className="space-y-6">
        <PageHeader title="Review the changes" description="A new version arrived in the same email thread as a contract you saved." actions={back} />
        <UpdateReview item={item} today={today} onApplied={saved} onSavedAsNew={saved} onDiscarded={discarded} />
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <PageHeader title="Review and save" description="Check what we found, change anything, then save it as a contract." actions={back} />
      <ItemEditor
        where={{ kind: "app", id }}
        item={item}
        today={today}
        signedIn
        onSaved={async (contractId) => {
          await engine.sync();
          void refreshMe();
          navigate(`/contracts/${contractId}`);
        }}
        onDiscarded={() => {
          void refreshMe();
          navigate("/inbox");
        }}
        onCancel={() => navigate("/inbox")}
      />
    </div>
  );
}
